import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Bell, BellRing, Check, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MONTHS = [
  { key: "2026-08", label: "AUG", name: "August" },
  { key: "2026-09", label: "SEP", name: "September" },
  { key: "2026-10", label: "OCT", name: "October" },
  { key: "2026-11", label: "NOV", name: "November" },
  { key: "2026-12", label: "DEC", name: "December" },
];

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function CalendarSection() {
  const { user, openAuth, setUser } = useAuth();
  const [days, setDays] = useState([]);
  const [month, setMonth] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mine, setMine] = useState({ dates: [], total: 0, streak: 0 });
  const [busy, setBusy] = useState(false);
  const today = todayIso();

  useEffect(() => {
    api.get("/activations").then((r) => {
      setDays(r.data.days);
      const currentMonth = today.slice(0, 7);
      const startMonth = r.data.start.slice(0, 7);
      setMonth(currentMonth >= startMonth && currentMonth <= "2026-12" ? currentMonth : startMonth);
    }).catch(() => toast.error("Couldn't load the calendar."));
  }, [today]);

  useEffect(() => {
    if (user) {
      api.get("/checkins/mine").then((r) => setMine(r.data)).catch(() => {});
    } else {
      setMine({ dates: [], total: 0, streak: 0 });
    }
  }, [user]);

  const monthDays = useMemo(() => days.filter((d) => d.date.startsWith(month || "")), [days, month]);
  const firstWeekday = useMemo(() => (monthDays.length ? new Date(monthDays[0].date + "T00:00:00").getDay() : 0), [monthDays]);
  const checked = useMemo(() => new Set(mine.dates), [mine]);

  const toggleCheckin = async (day) => {
    if (!user) {
      openAuth("register");
      return;
    }
    setBusy(true);
    try {
      if (checked.has(day.date)) {
        await api.delete(`/checkins/${day.date}`);
        toast.success("Unchecked. The day is yours again.");
      } else {
        await api.post("/checkins", { date: day.date });
        toast.success(`Countdown Day ${day.countdown} complete. Kindness looks good on you.`);
      }
      const r = await api.get("/checkins/mine");
      setMine(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const prettyDate = (iso) =>
    new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  return (
    <section id="calendar" className="border-t border-[#33240E]/15 bg-[#FFF6DF]/70 py-32" data-testid="calendar-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="mb-16 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.4em] text-[#8A5A0F]">120 Days of Be Kind</p>
            <h2 className="font-serif text-4xl font-black text-[#33240E] sm:text-5xl lg:text-6xl">
              The Activation<br /><span className="italic text-[#8A5A0F]">Calendar</span>
            </h2>
          </div>
          <div className="flex flex-col justify-end lg:col-span-5">
            <p className="max-w-md text-base font-light leading-relaxed text-muted-foreground">
              Every day from August 19 to the Ball holds one activation — some simple, some big.
              Days unlock as they arrive. Check off what you complete; your streak is your ticket.
            </p>
            {user && (
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A0F]" data-testid="calendar-score">
                  {mine.total} check-ins · {mine.streak}-day streak
                </p>
                <button
                  onClick={async () => {
                    try {
                      const next = !user.reminder_opt_in;
                      await api.post("/reminders/toggle", { enabled: next });
                      setUser({ ...user, reminder_opt_in: next });
                      toast.success(next ? "Morning activation emails are on — see you at sunrise." : "Daily reminders off.");
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                  data-testid="reminder-toggle"
                  className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E]/70 transition-colors duration-300 hover:text-[#33240E]"
                >
                  {user.reminder_opt_in ? <BellRing className="h-3.5 w-3.5 text-[#8A5A0F]" /> : <Bell className="h-3.5 w-3.5" />}
                  {user.reminder_opt_in ? "Daily reminder on" : "Daily reminder off"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mb-10 flex flex-wrap gap-2" data-testid="calendar-month-tabs">
          {MONTHS.map((m) => (
            <button key={m.key} onClick={() => setMonth(m.key)} data-testid={`month-tab-${m.label.toLowerCase()}`}
              className={`border px-6 py-2 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 ${
                month === m.key ? "border-[#8A5A0F] bg-[#33240E] text-[#F6E7C1]" : "border-[#33240E]/25 text-muted-foreground hover:border-[#8A5A0F] hover:text-[#8A5A0F]"
              }`}>
              {m.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px bg-[#33240E]/10 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="bg-[#FDF3D8] px-2 py-3 text-center">{d}</div>
          ))}
        </div>

        <motion.div
          key={month}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-7 gap-px bg-[#33240E]/10"
          data-testid="calendar-grid"
        >
          {Array.from({ length: firstWeekday }).map((_, i) => (
            <div key={`blank-${i}`} className="min-h-[72px] bg-[#FDF3D8] sm:min-h-[96px]" />
          ))}
          {monthDays.map((day) => {
            const isFuture = day.date > today;
            const isToday = day.date === today;
            const isChecked = checked.has(day.date);
            return (
              <button
                key={day.date}
                onClick={() => setSelected(day)}
                data-testid={`calendar-day-${day.date}`}
                className={`group relative min-h-[72px] bg-[#FDF3D8] p-2 text-left transition-colors duration-300 sm:min-h-[96px] sm:p-3 ${
                  isFuture ? "cursor-pointer text-muted-foreground/40 hover:bg-[#FFF9EA]" : "hover:bg-[#FFF9EA]"
                } ${isToday ? "ring-1 ring-inset ring-[#9E3C3C]" : ""} ${isChecked ? "bg-[#33240E]/10" : ""}`}
              >
                <span className={`font-mono text-xs ${isToday ? "text-[#8A5A0F]" : isChecked ? "text-[#8A5A0F]" : "text-[#33240E]/70"}`}>
                  {Number(day.date.slice(-2))}
                </span>
                <span className="absolute bottom-2 left-2 flex items-center gap-1 sm:bottom-3 sm:left-3">
                  {isFuture && <Lock className="h-3 w-3 text-muted-foreground/40" />}
                  {isChecked && <Check className="h-3.5 w-3.5 text-[#8A5A0F]" />}
                  {day.effort === "big" && !isFuture && <Sparkles className="h-3 w-3 text-velvet" />}
                </span>
                {isToday && (
                  <span className="absolute right-2 top-2 hidden font-mono text-[9px] uppercase tracking-[0.2em] text-[#8A5A0F] sm:block">Today</span>
                )}
              </button>
            );
          })}
        </motion.div>

        <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
          <DialogContent className="max-h-[92vh] overflow-y-auto border-[#33240E]/25 bg-[#FFF9EA] text-[#33240E] sm:max-w-lg" data-testid="activation-dialog">
            {selected && (
              <>
                <DialogHeader>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A5A0F]" data-testid="activation-date">
                    Countdown Day {selected.countdown} — {prettyDate(selected.date)}
                  </p>
                  <DialogTitle className="font-serif text-3xl font-bold" data-testid="activation-title">{selected.title}</DialogTitle>
                  {selected.description.replace(/[.’‘]/g, "").trim() !== selected.title.replace(/[.’‘]/g, "").trim() && (
                    <DialogDescription className="pt-2 text-base font-light leading-relaxed text-muted-foreground" data-testid="activation-description">
                      {selected.description}
                    </DialogDescription>
                  )}
                </DialogHeader>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] ${
                    selected.effort === "big" ? "border-velvet text-velvet" : "border-[#33240E]/25 text-muted-foreground"
                  }`} data-testid="activation-effort">
                    {selected.effort === "big" ? "Big Kindness" : "Simple Kindness"}
                  </span>
                  {selected.date > today ? (
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="activation-locked">
                      Unlocks {prettyDate(selected.date)}
                    </span>
                  ) : (
                    <button onClick={() => toggleCheckin(selected)} disabled={busy} data-testid="checkin-button"
                      className={`px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 disabled:opacity-50 ${
                        checked.has(selected.date)
                          ? "border border-[#8A5A0F] text-[#8A5A0F] hover:bg-[#33240E] hover:text-[#F6E7C1]"
                          : "bg-[#33240E] text-[#F6E7C1] hover:bg-[#4A3517]"
                      }`}>
                      {busy ? "Saving..." : checked.has(selected.date) ? "Completed — Undo" : user ? "I Did This" : "Sign In to Check Off"}
                    </button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
